import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { Order, OrderStatus, PaymentStatus } from '../../../lib/api/models';

// Mapeado de estados para mostrarlos en español y con colores
const orderStatusMap: Record<OrderStatus, { label: string, color: string }> = {
  'pending': { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' },
  'processing': { label: 'Procesando', color: 'bg-blue-100 text-blue-800' },
  'shipped': { label: 'Enviado', color: 'bg-indigo-100 text-indigo-800' },
  'delivered': { label: 'Entregado', color: 'bg-green-100 text-green-800' },
  'cancelled': { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
  'payment_failed': { label: 'Pago Fallido', color: 'bg-red-100 text-red-800' },
  'refunded': { label: 'Reembolsado', color: 'bg-purple-100 text-purple-800' }
};

const paymentStatusMap: Record<PaymentStatus, { label: string, color: string }> = {
  'pending': { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' },
  'completed': { label: 'Completado', color: 'bg-green-100 text-green-800' },
  'failed': { label: 'Fallido', color: 'bg-red-100 text-red-800' },
  'refunded': { label: 'Reembolsado', color: 'bg-purple-100 text-purple-800' }
};

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Modo edición completa

  // Estados para los formularios de actualización simple
  const [orderStatus, setOrderStatus] = useState<OrderStatus>('pending');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Estados para edición avanzada
  const [editForm, setEditForm] = useState<{
    products: any[];
    shippingAddress: any;
    billingAddress: any;
    shippingCost: number;
    paymentMethod: string;
    customer: string | null;
    customerInfo: any;
    shippingMethod: string;
    estimatedDelivery?: string;
  }>({
    products: [],
    shippingAddress: {},
    billingAddress: {},
    shippingCost: 0,
    paymentMethod: '',
    customer: null,
    customerInfo: {},
    shippingMethod: '',
    estimatedDelivery: ''
  });

  // Estado para búsqueda de productos/clientes
  const [productSearch, setProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [foundProducts, setFoundProducts] = useState<any[]>([]);
  const [foundCustomers, setFoundCustomers] = useState<any[]>([]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const response = await apiManager.orders.getOrder(id);
        if (response.success && response.data) {
          setOrder(response.data);
          // Inicializar los estados del formulario con los valores actuales
          setOrderStatus(response.data.status);
          setPaymentStatus(response.data.paymentStatus);
          setTrackingNumber(response.data.trackingNumber || '');
          setNotes(response.data.notes || '');

          // Inicializar form de edición
          setEditForm({
            products: response.data.products.map(p => ({
              productId: typeof p.product === 'string' ? p.product : p.product.id,
              name: p.name,
              quantity: p.quantity,
              price: p.price,
              variantSku: p.variant?.sku,
              variant: p.variant,
              initialData: p // Guardar ref original si ayuda
            })),
            shippingAddress: { ...response.data.shippingAddress },
            billingAddress: { ...(response.data.billingAddress || response.data.shippingAddress) },
            shippingCost: response.data.shippingCost,
            paymentMethod: response.data.paymentMethod,
            customer: response.data.customer ? response.data.customer.id : null,
            customerInfo: { ...(response.data.customerInfo || {}) },
            shippingMethod: response.data.shippingMethod,
            estimatedDelivery: response.data.estimatedDelivery ? response.data.estimatedDelivery.split('T')[0] : ''
          });
        } else {
          setError(
            'Error al cargar el pedido'
          );
        }
      } catch (err) {
        console.error('Error al cargar el pedido:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handleOrderStatusUpdate = async () => {
    if (!id || !order) return;

    setIsUpdating(true);
    try {
      // Si se informa fecha estimada al pasar a processing, actualizarla primero.
      if (orderStatus === 'processing' && editForm.estimatedDelivery) {
        await apiManager.orders.updateEstimatedDelivery(id, editForm.estimatedDelivery);
      }

      const response = await apiManager.orders.updateOrderStatus(id, orderStatus);
      if (response.success) {
        setOrder({
          ...order,
          status: orderStatus,
          estimatedDelivery: editForm.estimatedDelivery || order.estimatedDelivery
        });
      } else {
        setError(
          'Error al actualizar el estado del pedido'
        );
      }
    } catch (err) {
      console.error('Error al actualizar el estado del pedido:', err);
      setError('Error al actualizar el estado del pedido');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePaymentStatusUpdate = async () => {
    if (!id || !order) return;

    setIsUpdating(true);
    try {
      const response = await apiManager.orders.updatePaymentStatus(id, paymentStatus);
      if (response.success) {
        setOrder({
          ...order,
          paymentStatus: paymentStatus
        });
      } else {
        setError(
          'Error al actualizar el estado del pago'
        );
      }
    } catch (err) {
      console.error('Error al actualizar el estado del pago:', err);
      setError('Error al actualizar el estado del pago');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTrackingUpdate = async () => {
    if (!id || !order) return;

    setIsUpdating(true);
    try {
      const response = await apiManager.orders.addTrackingNumber(id, trackingNumber);
      if (response.success) {
        setOrder({
          ...order,
          trackingNumber: trackingNumber
        });
      } else {
        setError(
          'Error al actualizar el número de seguimiento'
        );
      }
    } catch (err) {
      console.error('Error al actualizar el número de seguimiento:', err);
      setError('Error al actualizar el número de seguimiento');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handlers para búsqueda
  const handleSearchProducts = async (query: string) => {
    setProductSearch(query);
    if (query.length < 3) return;

    try {
      const response = await apiManager.products.getProducts({ search: query, limit: 10 });
      if (response.success && response.data) {
        setFoundProducts(response.data);
      }
    } catch (err) {
      console.error('Error buscando productos:', err);
    }
  };

  const handleSearchCustomers = async (query: string) => {
    setCustomerSearch(query);
    if (query.length < 3) return;

    try {
      const response = await apiManager.users.getUsers({ search: query, limit: 5, filter: { role: 'customer' } });
      if (response.success && response.data) {
        setFoundCustomers(response.data);
      }
    } catch (err) {
      console.error('Error buscando clientes:', err);
    }
  };

  // Handlers para manipular productos en edición
  const handleAddProduct = (product: any, variant: any = null) => {
    const newItem = {
      productId: product.id,
      name: product.name + (variant ? ` - ${variant.sku}` : ''),
      quantity: 1,
      price: variant ? (variant.price || product.price) : product.price,
      variantSku: variant ? variant.sku : undefined,
      variant: variant,
      product: product // Guardamos referencia completa
    };

    setEditForm({
      ...editForm,
      products: [...editForm.products, newItem]
    });
    setProductSearch('');
    setFoundProducts([]);
  };

  const handleRemoveProduct = (index: number) => {
    const newProducts = [...editForm.products];
    newProducts.splice(index, 1);
    setEditForm({
      ...editForm,
      products: newProducts
    });
  };

  const handleProductChange = (index: number, field: string, value: any) => {
    const newProducts = [...editForm.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setEditForm({
      ...editForm,
      products: newProducts
    });
  };

  const handleMoveProduct = (index: number, direction: 'up' | 'down') => {
    const newProducts = [...editForm.products];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    // Verificar límites
    if (newIndex < 0 || newIndex >= newProducts.length) return;

    // Intercambiar posiciones
    [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];

    setEditForm({
      ...editForm,
      products: newProducts
    });
  };

  // Handlers para guardar cambios generales
  const handleEditSave = async () => {
    if (!id) return;
    setIsUpdating(true);
    try {
      const productsPayload = editForm.products.map(p => ({
        productId: p.productId,
        quantity: Number(p.quantity),
        price: Number(p.price),
        variantSku: p.variantSku
      }));

      const payload = {
        products: productsPayload,
        shippingAddress: editForm.shippingAddress,
        billingAddress: editForm.billingAddress,
        shippingCost: Number(editForm.shippingCost),
        paymentMethod: editForm.paymentMethod,
        customer: editForm.customer,
        status: orderStatus, // Usamos el estado sincronizado
        trackingNumber: trackingNumber || undefined,
        notes: notes || undefined,
        estimatedDelivery: editForm.estimatedDelivery || undefined
      };

      const response = await apiManager.orders.updateOrderAdmin(id, payload);

      if (response.success && response.data) {
        setOrder(response.data);
        setIsEditing(false);
        setOrderStatus(response.data.status);
        setPaymentStatus(response.data.paymentStatus);
        setTrackingNumber(response.data.trackingNumber || '');
        setNotes(response.data.notes || '');
      } else {
        setError('Error al guardar los cambios del pedido');
      }
    } catch (err) {
      console.error('Error al guardar cambios:', err);
      setError('Error al guardar cambios');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNotesUpdate = async () => {
    if (!id || !order) return;

    setIsUpdating(true);
    try {
      const response = await apiManager.orders.updateOrder(id, { notes });
      if (response.success) {
        setOrder({
          ...order,
          notes: notes
        });
      } else {
        setError('Error al actualizar las notas');
      }
    } catch (err) {
      console.error('Error al actualizar las notas:', err);
      setError('Error al actualizar las notas');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Detalle de Pedido">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      ) : order ? (
        <div className="space-y-8">
          {/* Cabecera con identificadores y acciones */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Pedido #{order.id.slice(-8)} {isEditing && <span className="text-indigo-600">(Editando)</span>}
                </h2>
                <p className="text-gray-500">
                  Fecha: {formatDate(order.createdAt)}
                </p>
              </div>

              <div className="mt-4 md:mt-0 space-y-2 md:space-y-0 md:space-x-2 flex flex-col md:flex-row">
                {!isEditing && (
                  <>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${orderStatusMap[order.status].color}`}>
                      {orderStatusMap[order.status].label}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${paymentStatusMap[order.paymentStatus].color}`}>
                      Pago: {paymentStatusMap[order.paymentStatus].label}
                    </span>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={isUpdating}
                  className={`px-4 py-2 border rounded-md text-sm font-medium ${isEditing
                    ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    : 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700'
                    }`}
                >
                  {isEditing ? 'Cancelar Edición' : 'Editar Pedido Completo'}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/admin/orders')}
                  className="text-gray-700 border border-gray-300 px-3 py-1 rounded-md text-sm hover:bg-gray-50"
                >
                  Volver
                </button>
              </div>
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-6">
              {/* FORMULARIO DE EDICIÓN */}

              {/* 1. Cliente */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reasignar Cliente</h3>
                <div className="mb-4">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => handleSearchCustomers(e.target.value)}
                      placeholder="Buscar cliente por nombre o email..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {foundCustomers.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-200">
                      {foundCustomers.map(c => (
                        <div
                          key={c.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                          onClick={() => {
                            setEditForm({
                              ...editForm,
                              customer: c.id,
                              customerInfo: { name: c.name, email: c.email, phone: c.phone, nif: c.nif }
                            });
                            setFoundCustomers([]);
                            setCustomerSearch('');
                          }}
                        >
                          <div>
                            <p className="font-medium text-gray-900">{c.name}</p>
                            <p className="text-sm text-gray-500">{c.email}</p>
                          </div>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Seleccionar</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700"><span className="font-bold">Cliente Actual (ID):</span> {editForm.customer || 'No asignado'}</p>
                  {editForm.customerInfo && (
                    <p className="text-sm text-gray-700"><span className="font-bold">Datos:</span> {editForm.customerInfo.name} ({editForm.customerInfo.email})</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 2. Dirección de Envío */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Dirección de Envío</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.shippingAddress.street || ''}
                      onChange={(e) => setEditForm({ ...editForm, shippingAddress: { ...editForm.shippingAddress, street: e.target.value } })}
                      placeholder="Calle y número"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editForm.shippingAddress.postalCode || ''}
                        onChange={(e) => setEditForm({ ...editForm, shippingAddress: { ...editForm.shippingAddress, postalCode: e.target.value } })}
                        placeholder="Código Postal"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="text"
                        value={editForm.shippingAddress.city || ''}
                        onChange={(e) => setEditForm({ ...editForm, shippingAddress: { ...editForm.shippingAddress, city: e.target.value } })}
                        placeholder="Ciudad"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={editForm.shippingAddress.country || ''}
                      onChange={(e) => setEditForm({ ...editForm, shippingAddress: { ...editForm.shippingAddress, country: e.target.value } })}
                      placeholder="País"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                {/* 3. Dirección de Facturación */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Dirección de Facturación</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.billingAddress.street || ''}
                      onChange={(e) => setEditForm({ ...editForm, billingAddress: { ...editForm.billingAddress, street: e.target.value } })}
                      placeholder="Calle y número"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editForm.billingAddress.postalCode || ''}
                        onChange={(e) => setEditForm({ ...editForm, billingAddress: { ...editForm.billingAddress, postalCode: e.target.value } })}
                        placeholder="Código Postal"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="text"
                        value={editForm.billingAddress.city || ''}
                        onChange={(e) => setEditForm({ ...editForm, billingAddress: { ...editForm.billingAddress, city: e.target.value } })}
                        placeholder="Ciudad"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={editForm.billingAddress.country || ''}
                      onChange={(e) => setEditForm({ ...editForm, billingAddress: { ...editForm.billingAddress, country: e.target.value } })}
                      placeholder="País"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* 4. Productos */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Productos</h3>

                  {/* Buscador de productos para añadir */}
                  <div className="relative w-1/2">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => handleSearchProducts(e.target.value)}
                      placeholder="Añadir producto (buscar por nombre)..."
                      className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm"
                    />
                    {foundProducts.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {foundProducts.map(p => (
                          <div key={p.id}>
                            <div
                              className="p-2 hover:bg-gray-50 cursor-pointer font-medium border-b border-gray-100"
                              onClick={() => !p.variants?.length && handleAddProduct(p)}
                            >
                              {p.name} {!p.variants?.length && <span className="text-gray-500 ml-2">({p.price}€)</span>}
                            </div>
                            {p.variants && p.variants.map((v: any) => (
                              <div
                                key={`${p.id}-${v.sku}`}
                                className="p-2 pl-6 hover:bg-gray-50 cursor-pointer text-sm text-gray-600 flex justify-between"
                                onClick={() => handleAddProduct(p, v)}
                              >
                                <span>{v.sku} {v.attributes?.size}</span>
                                <span>{v.price}€</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio (€)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-6 py-3 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {editForm.products.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.name}
                            {item.variantSku && <span className="block text-xs text-gray-500">REF: {item.variantSku}</span>}
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {(Number(item.price) * Number(item.quantity)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="flex flex-col">
                                <button
                                  onClick={() => handleMoveProduct(index, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Mover arriba"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleMoveProduct(index, 'down')}
                                  disabled={index === editForm.products.length - 1}
                                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Mover abajo"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>
                              <button onClick={() => handleRemoveProduct(index)} className="text-red-600 hover:text-red-900 text-sm">Eliminar</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 5. Costes y Pago */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Costes de Envío</h3>
                  <label className="block text-sm font-medium text-gray-700">Coste (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.shippingCost}
                    onChange={(e) => setEditForm({ ...editForm, shippingCost: Number(e.target.value) })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  />
                  <div className="mt-4">
                    <p className="font-bold text-lg text-right">
                      Total Pedido: {(editForm.products.reduce((acc, item) => acc + (Number(item.price) * Number(item.quantity)), 0) + Number(editForm.shippingCost)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Método y Estado de Pago</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Método de Pago</label>
                      <select
                        value={editForm.paymentMethod}
                        onChange={(e) => setEditForm({ ...editForm, paymentMethod: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="card">Tarjeta de crédito</option>
                        <option value="bank_transfer">Transferencia bancaria</option>
                        <option value="paypal">PayPal</option>
                        <option value="cash">Efectivo</option>
                        <option value="stripe">Stripe</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Estado de Pago</label>
                      <select
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="completed">Completado</option>
                        <option value="failed">Fallido</option>
                        <option value="refunded">Reembolsado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Estado del Pedido</label>
                      <select
                        value={orderStatus}
                        onChange={(e) => setOrderStatus(e.target.value as OrderStatus)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="processing">Procesando</option>
                        <option value="shipped">Enviado</option>
                        <option value="delivered">Entregado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de Guardar */}
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={isUpdating}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50"
                >
                  {isUpdating ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          ) : (
            // VISTA NORMAL (NO EDICIÓN) - Código existente
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Información del cliente */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Información del Cliente</h3>

                {order.customer ? (
                  <div className="space-y-3">
                    <p><span className="font-medium">Nombre:</span> {order.customer.name}</p>
                    <p><span className="font-medium">Email:</span> {order.customer.email}</p>
                    {order.customer.phone && (
                      <p><span className="font-medium">Teléfono:</span> {order.customer.phone}</p>
                    )}
                    {order.customer.nif && (
                      <p><span className="font-medium">NIF/CIF:</span> {order.customer.nif}</p>
                    )}
                  </div>
                ) : order.customerInfo ? (
                  <div className="space-y-3">
                    <p><span className="font-medium">Nombre:</span> {order.customerInfo.name}</p>
                    <p><span className="font-medium">Email:</span> {order.customerInfo.email}</p>
                    {order.customerInfo.phone && (
                      <p><span className="font-medium">Teléfono:</span> {order.customerInfo.phone}</p>
                    )}
                    {order.customerInfo.nif && (
                      <p><span className="font-medium">NIF/CIF:</span> {order.customerInfo.nif}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">Cliente anónimo</p>
                )}

                {/* Info del comercial que creó el pedido */}
                {(order as any).createdBy && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-indigo-700 mb-2">Pedido creado por comercial</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="font-medium">Comercial:</span> {(order as any).createdBy.name}</p>
                      <p><span className="font-medium">Email:</span> {(order as any).createdBy.email}</p>
                      {(order as any).createdBy.commissionPercentage > 0 && (
                        <>
                          <p><span className="font-medium">% Comisión:</span> {(order as any).createdBy.commissionPercentage}%</p>
                          <p className="text-green-700 font-semibold">
                            <span className="font-medium">Comisión:</span> {((order.totalAmount * (order as any).createdBy.commissionPercentage) / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Dirección de envío */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dirección de Envío</h3>
                <div className="space-y-2">
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.postalCode}, {order.shippingAddress.city}</p>
                  <p>{order.shippingAddress.country}</p>
                </div>

                <div className="mt-4">
                  <p><span className="font-medium">Método de envío:</span> {order.shippingMethod}</p>
                  <p><span className="font-medium">Coste de envío:</span> {order.shippingCost.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                  {order.trackingNumber && (
                    <p><span className="font-medium">Número de seguimiento:</span> {order.trackingNumber}</p>
                  )}
                </div>
              </div>

              {/* Información de pago */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Información de Pago</h3>
                <div className="space-y-3">
                  <p><span className="font-medium">Método de pago:</span> {order.paymentMethod === 'card' ? 'Tarjeta de crédito' :
                    order.paymentMethod === 'bank_transfer' ? 'Transferencia bancaria' :
                      order.paymentMethod === 'paypal' ? 'PayPal' :
                        order.paymentMethod === 'cash' ? 'Efectivo' :
                          order.paymentMethod === 'stripe' ? 'Stripe' :
                            order.paymentMethod}</p>
                  <p><span className="font-medium">Estado del pago:</span> <span className={`px-2 py-0.5 rounded text-xs ${paymentStatusMap[order.paymentStatus].color}`}>{paymentStatusMap[order.paymentStatus].label}</span></p>

                  {order.discount && (
                    <div className="mt-2">
                      <p><span className="font-medium">Cupón aplicado:</span> {order.discount.code}</p>
                      <p>
                        <span className="font-medium">Descuento:</span> {order.discount.percentage ? `${order.discount.percentage}%` :
                          order.discount.amount ? order.discount.amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : ''}
                      </p>
                    </div>
                  )}
                  {(order as any).userDiscountAtOrderTime > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 rounded">
                      <p><span className="font-medium text-blue-700">Descuento personal del usuario:</span> {(order as any).userDiscountAtOrderTime}%</p>
                    </div>
                  )}
                  {(order as any).prontoPagoDiscountAtOrderTime > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 rounded">
                      <p><span className="font-medium text-amber-700">Pronto pago:</span> {(order as any).prontoPagoDiscountAtOrderTime}%</p>
                      <p className="text-sm text-amber-600">
                        Total con pronto pago: {(order.totalAmount * (1 - (order as any).prontoPagoDiscountAtOrderTime / 100)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Productos del pedido */}
              <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 p-6 border-b border-gray-200">Productos</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Producto
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Precio
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cantidad
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.products.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {typeof item.product !== 'string' && item.product.images && item.product.images[0] && (
                                <div className="flex-shrink-0 h-10 w-10 mr-4">
                                  <img className="h-10 w-10 rounded-md object-cover" src={item.product.images[0].url} alt={item.name} />
                                </div>
                              )}
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                {item.variant?.sku ? (
                                  <div className="text-sm text-gray-500">{item.variant.sku}</div>
                                ) : typeof item.product !== 'string' && (
                                  <div className="text-sm text-gray-500">{item.product.sku}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              {(() => {
                                const pbd = (item as any).priceBeforeDiscount || (item as any).priceBeforeGlobalDiscount;
                                const dInfo = (item as any).discountInfo || (item as any).globalDiscountInfo;
                                const dSource = (item as any).discountSource;
                                if (pbd && pbd > item.price) {
                                  return (
                                    <>
                                      <span className="line-through text-gray-400">
                                        {pbd.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                                      </span>
                                      <span className="ml-2 text-green-600 font-medium">
                                        {item.price.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                                      </span>
                                      {dInfo && (
                                        <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                          {dInfo.displayValue}
                                        </span>
                                      )}
                                      {dSource && (
                                        <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${dSource === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                          {dSource === 'user' ? 'Dto. usuario' : 'Dto. global'}
                                        </span>
                                      )}
                                    </>
                                  );
                                }
                                return item.price.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(item.price * item.quantity).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                          Subtotal
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {(order.totalAmount - order.shippingCost).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                          Gastos de envío
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {order.shippingCost.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-base font-bold text-gray-900 text-right">
                          Total
                        </td>
                        <td className="px-6 py-4 text-base font-bold text-gray-900">
                          {order.totalAmount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </td>
                      </tr>
                      {(order as any).prontoPagoDiscountAtOrderTime > 0 && (
                        <tr className="bg-amber-50">
                          <td colSpan={3} className="px-6 py-3 text-sm font-medium text-amber-800 text-right">
                            Con pronto pago ({(order as any).prontoPagoDiscountAtOrderTime}%)
                          </td>
                          <td className="px-6 py-3 text-sm font-bold text-amber-800">
                            {(order.totalAmount * (1 - (order as any).prontoPagoDiscountAtOrderTime / 100)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Actualizar estados */}
              <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Actualizar estado del pedido */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Actualizar Estado del Pedido</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="orderStatus" className="block text-sm font-medium text-gray-700">Estado</label>
                      <select
                        id="orderStatus"
                        value={orderStatus}
                        onChange={(e) => setOrderStatus(e.target.value as OrderStatus)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="processing">Procesando</option>
                        <option value="shipped">Enviado</option>
                        <option value="delivered">Entregado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                    {
                      orderStatus === 'processing' && (
                        <div>
                          <label htmlFor="estimatedDelivery" className="block text-sm font-medium text-gray-700">Fecha de Entrega Estimada (opcional)</label>
                          <input
                            id="estimatedDelivery"
                            type="date"
                            value={editForm.estimatedDelivery}
                            onChange={(e) => setEditForm({ ...editForm, estimatedDelivery: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      )
                    }
                    <button
                      type="button"
                      onClick={handleOrderStatusUpdate}
                      disabled={isUpdating || orderStatus === order.status}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isUpdating ? 'Actualizando...' : 'Actualizar Estado'}
                    </button>
                  </div>
                </div>

                {/* Actualizar estado del pago */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Actualizar Estado del Pago</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">Estado del Pago</label>
                      <select
                        id="paymentStatus"
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="completed">Completado</option>
                        <option value="failed">Fallido</option>
                        <option value="refunded">Reembolsado</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handlePaymentStatusUpdate}
                      disabled={isUpdating || paymentStatus === order.paymentStatus}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isUpdating ? 'Actualizando...' : 'Actualizar Estado de Pago'}
                    </button>
                  </div>
                </div>

                {/* Actualizar número de seguimiento */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Número de Seguimiento</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700">Número</label>
                      <input
                        type="text"
                        id="trackingNumber"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Introducir número de seguimiento"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleTrackingUpdate}
                      disabled={isUpdating || trackingNumber === order.trackingNumber}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isUpdating ? 'Actualizando...' : 'Actualizar Seguimiento'}
                    </button>
                  </div>
                </div>

                {/* Notas del pedido */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notas</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notas internas</label>
                      <textarea
                        id="notes"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Añadir notas internas sobre el pedido"
                      ></textarea>
                    </div>
                    <button
                      type="button"
                      onClick={handleNotesUpdate}
                      disabled={isUpdating || notes === order.notes}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isUpdating ? 'Actualizando...' : 'Actualizar Notas'}
                    </button>
                  </div>
                </div>
              </div>


              {/* Historial de Cambios */}
              {order.history && order.history.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6 md:col-span-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Historial de Actividad</h3>
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {order.history.slice().reverse().map((entry, eventIdx) => (
                        <li key={eventIdx}>
                          <div className="relative pb-8">
                            {eventIdx !== order.history!.length - 1 ? (
                              <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div>
                                <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${entry.action === 'create' ? 'bg-green-500' :
                                  entry.action === 'status_change' ? 'bg-blue-500' :
                                    entry.action === 'merge' ? 'bg-purple-500' :
                                      entry.action === 'update' ? 'bg-orange-500' :
                                        'bg-gray-500'
                                  }`}>
                                  <svg className="h-5 w-5 text-white"
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    {entry.action === 'create' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    ) : entry.action === 'status_change' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    ) : entry.action === 'merge' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    )}
                                  </svg>
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-gray-500">
                                    {entry.description}{' '}
                                  </p>
                                </div>
                                <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                  <time dateTime={entry.date}>{new Date(entry.date).toLocaleString('es-ES')}</time>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontró el pedido</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default OrderDetailPage;
